import type { Section } from "@/types";

export type ResolveRosterAccessibleSectionsInput = {
  canViewSection: boolean;
  isSectionStaff: boolean;
  assignedSectionIds: string[];
  allSections: Section[];
};

/** Preserve assigned section order while mapping ids to GraphQL sections. */
export function mapAssignedSectionIdsToGraphqlSections(
  assignedSectionIds: string[],
  allSections: Section[]
): Section[] {
  if (assignedSectionIds.length === 0) {
    return [];
  }

  const byLegacyId = new Map(allSections.map((section) => [section.legacyId, section]));
  const byId = new Map(allSections.map((section) => [section.id, section]));
  const resolved: Section[] = [];
  const seen = new Set<string>();

  for (const assignedSectionId of assignedSectionIds) {
    const section = byLegacyId.get(assignedSectionId) ?? byId.get(assignedSectionId);
    if (!section || seen.has(section.id)) {
      continue;
    }

    seen.add(section.id);
    resolved.push(section);
  }

  return resolved;
}

export function resolveRosterAccessibleSections({
  canViewSection,
  isSectionStaff,
  assignedSectionIds,
  allSections
}: ResolveRosterAccessibleSectionsInput): Section[] {
  if (!canViewSection) {
    return [];
  }

  if (isSectionStaff) {
    return mapAssignedSectionIdsToGraphqlSections(assignedSectionIds, allSections);
  }

  return [...allSections].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" })
  );
}

export function resolveDefaultRosterSectionId(sections: Section[], preferredSectionId?: string | null): string | null {
  if (sections.length === 0) {
    return null;
  }

  if (preferredSectionId && sections.some((section) => section.id === preferredSectionId)) {
    return preferredSectionId;
  }

  return sections[0].id;
}
